import React, { useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Autocomplete, Box, CircularProgress, Grid, IconButton, MenuItem, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useReactToPrint } from 'react-to-print';

import AppForm from '@/__core/components/AppForm';
import { UserOptionDisplay } from '@/__core/components';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { AddButtonAutocomplete } from '@/__core/hocs';
import OrderAutocomplete, { OrderAutocompleteItem } from '@/modules/commons/components/OrderAutocomplete';
import { RequestMaterialEmployeeOption } from '@/modules/inventory/requestMaterial/types';
import OrderPrintTemplateRuntime from '@/modules/orders/components/OrderPrintTemplateRuntime';
import DeleteConfirmationModal from '@/__core/components/DeleteConfirmationModal';
import FileAttachmentField from '@/__core/components/FileAttachmentField';
import axiosInstance from '@/setup/axios/axiosInstance';
import {
  deleteDocument,
  getContentTypeId,
  getDocumentsByObject,
  type Document as AppDocument,
} from '@/modules/documents/redux/DocumentCRUD';

import { InventoryIssueFormData, InventoryIssueFormLine } from '../types';
import IssueLinesTable from './IssueLinesTable';

interface IssueFormProps {
  formData: InventoryIssueFormData;
  statusOptions: Array<{ id: string; name: string; code?: string; color?: string }>;
  warehouseOptions: Array<{ id: string; name: string; is_default?: boolean }>;
  orderOptions: OrderAutocompleteItem[];
  orderLoading?: boolean;
  itemOptions: Array<{ id: string; name: string; code: string; unit_id?: string; unit_name?: string }>;
  uomOptionsByLineId?: Record<number, Array<{ id: string; name: string }>>;
  itemLoading?: boolean;
  employeeOptions?: RequestMaterialEmployeeOption[];
  stockByLineId?: Record<number, { on_hand_qty: number; allocated_qty: number; available_qty: number; last_price?: number; uom_name?: string } | null>;
  stockLoadingByLineId?: Record<number, boolean>;
  stockErrorByLineId?: Record<number, boolean>;
  onChangeForm: (updates: Partial<InventoryIssueFormData>) => void;
  onChangeLine: (lineId: number, updates: Partial<InventoryIssueFormLine>) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: number) => void;
  onSave: (files: File[]) => Promise<string | boolean | void> | string | boolean | void;
  issueId?: string;
  canEdit?: boolean;
  onCreateItem?: (item: { id: string; name: string; code: string; unit_id?: string; unit_name?: string }) => void;
  canCreateItem?: boolean;
  searchItems?: (query: string) => Promise<Array<{ id: string; name: string; code: string; unit_id?: string; unit_name?: string }>>;
  searchLoading?: boolean;
  saving?: boolean;
  /** Only enable save after user has edited the form */
  isDirty?: boolean;
  hideActionBar?: boolean;
}

export default function IssueForm({
  formData,
  statusOptions,
  warehouseOptions,
  orderOptions,
  orderLoading = false,
  itemOptions,
  uomOptionsByLineId = {},
  itemLoading = false,
  employeeOptions = [],
  stockByLineId = {},
  stockLoadingByLineId = {},
  stockErrorByLineId = {},
  onChangeForm,
  onChangeLine,
  onAddLine,
  onRemoveLine,
  onSave,
  issueId,
  canEdit = true,
  onCreateItem,
  canCreateItem = false,
  searchItems,
  searchLoading = false,
  saving = false,
  isDirty = false,
  hideActionBar = false,
}: IssueFormProps) {
  const readOnly = !canEdit || formData.status_is_posted === true;

  // Print state
  const printRef = useRef<HTMLDivElement>(null);
  const [printType] = useState<string>('issue');
  const [printKey, setPrintKey] = useState(0);
  const [printLoading, setPrintLoading] = useState(false);
  const pendingPrintRef = useRef(false);

  const triggerPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Phieu-xuat-${formData.code || 'new'}`,
    pageStyle: `@page { size: A4 portrait; margin: 10mm; } @media print { body { -webkit-print-color-adjust: exact; } .print-container { width: 100% !important; } }`,
  });

  const handlePrint = () => {
    setPrintKey((k) => k + 1);
    setPrintLoading(true);
    pendingPrintRef.current = true;
  };

  const handlePrintReady = () => {
    if (!pendingPrintRef.current) return;
    pendingPrintRef.current = false;
    setTimeout(() => {
      setPrintLoading(false);
      triggerPrint();
    }, 100);
  };

  const buildPrintData = () => ({
    order_code: formData.code,
    issued_at: formData.issued_at || '',
    warehouse_name: formData.store_name,
    created_by_name: formData.issued_by_name || '',
    issued_by_name: formData.issued_by_name || '',
    received_by_name: formData.received_by_name || '',
    order_no: formData.order_no || '',
    items: (formData.lines || []).map((line) => ({
      item_code: line.item_code,
      product_name: line.item_name,
      color: '',
      quantity: line.issued_qty || 0,
      unit_name: line.uom_name,
      unit_price: line.unit_cost || 0,
      total_price: (line.issued_qty || 0) * (line.unit_cost || 0),
      note: line.note || '',
    })),
  });

  const formatEmployeeName = (raw?: string) => {
    if (!raw) return '';
    return raw.replace(/^[^\s-]+\s*-\s*/i, '').trim();
  };
  const currentStatusValue = typeof (formData as any).status === 'object'
    ? String((formData as any).status?.id || (formData as any).status?.code || '')
    : String(formData.status || '');
  const selectedStatus = statusOptions.find((option) =>
    String(option.id).toLowerCase() === currentStatusValue.toLowerCase()
    || String(option.code || '').toLowerCase() === currentStatusValue.toLowerCase()
  ) || undefined;
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = React.useState<AppDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = React.useState(false);
  const [pendingDeleteType, setPendingDeleteType] = React.useState<'file' | 'document' | null>(null);
  const [pendingDeleteIndex, setPendingDeleteIndex] = React.useState<number | null>(null);
  const [pendingDeleteDocId, setPendingDeleteDocId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!issueId) {
      setUploadedDocuments([]);
      setLoadingDocuments(false);
      return;
    }
    let active = true;
    setLoadingDocuments(true);
    void (async () => {
      try {
        const contentTypeId = await getContentTypeId('issue');
        const documents = await getDocumentsByObject(contentTypeId, issueId);
        if (active) setUploadedDocuments(documents);
      } catch {
        if (active) setUploadedDocuments([]);
      } finally {
        if (active) setLoadingDocuments(false);
      }
    })();
    return () => { active = false; };
  }, [issueId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setSelectedFiles((current) => [...current, ...files]);
    event.target.value = '';
  };

  const handleSaveWithFiles = async () => {
    const hadPendingFiles = selectedFiles.length > 0;
    const saved = await onSave(selectedFiles);
    if (saved !== false) {
      setSelectedFiles([]);
      const savedIssueId = typeof saved === 'string' ? saved : issueId;
      if (savedIssueId && hadPendingFiles) {
        setLoadingDocuments(true);
        try {
          const contentTypeId = await getContentTypeId('issue');
          setUploadedDocuments(await getDocumentsByObject(contentTypeId, savedIssueId));
        } catch { /* silently fail */ }
        finally { setLoadingDocuments(false); }
      }
    }
  };

  const confirmDeleteAttachment = async () => {
    if (pendingDeleteType === 'file' && pendingDeleteIndex !== null) {
      setSelectedFiles((current) => current.filter((_file, index) => index !== pendingDeleteIndex));
    }
    if (pendingDeleteType === 'document' && pendingDeleteDocId) {
      try {
        await deleteDocument(pendingDeleteDocId);
        setUploadedDocuments((current) => current.filter((doc) => doc.id !== pendingDeleteDocId));
      } catch { /* silently fail */ }
    }
    setPendingDeleteType(null);
    setPendingDeleteIndex(null);
    setPendingDeleteDocId(null);
  };

  const handleDocumentDownload = async (documentId: string, preview = false) => {
    try {
      const response = await axiosInstance.get(`documents/${documentId}/download/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      if (preview) {
        window.open(url, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        return;
      }
      const doc = uploadedDocuments.find((item) => item.id === documentId);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = doc?.name || 'download';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { /* silently fail */ }
  };

  return (
    <>
    <AppForm
      onSubmit={handleSaveWithFiles}
      onCancel={() => {}}
      submitting={saving}
      submitDisabled={saving || readOnly || (!isDirty && selectedFiles.length === 0)}
      submitLabel='Lưu phiếu xuất'
      submittingLabel='Đang lưu...'
      containerSx={{
        m: 0,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
      hideActionBar={hideActionBar}
    >
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={sharedFormStyles.sectionTitle}>Thông tin phiếu xuất</Typography>
          {issueId && (
            <Tooltip title='In phiếu xuất'>
              <IconButton size='small' onClick={handlePrint} sx={{ color: '#64748b', '&:hover': { color: '#2563eb' } }}>
                <PrintIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Nội dung XK' labelWidth={100}>
              <TextField size='small' value={formData.name} onChange={(event) => onChangeForm({ name: event.target.value })} fullWidth disabled={readOnly} placeholder='VD: Xuất kho phụ kiện cơ bản cho đơn hàng A' />
            </LabeledField>
            <LabeledField label='Đơn hàng' labelWidth={100}>
              <OrderAutocomplete
                orders={orderOptions}
                value={formData.order_id || null}
                onChange={(orderId) => onChangeForm({ order_id: orderId || '' })}
                placeholder='Chọn đơn hàng'
                disabled={readOnly}
                loading={orderLoading}
                fullWidth
                noOptionsText='Không tìm thấy đơn hàng'
                sx={sharedFormStyles.inputArrowAlign}
              />
            </LabeledField>
            <LabeledField label='Ngày XK' labelWidth={100}>
              <DatePicker
                value={formData.issued_at ? new Date(formData.issued_at) : new Date()}
                onChange={(date) => onChangeForm({ issued_at: date ? (date as Date).toISOString().slice(0, 10) : null })}
                disabled={readOnly}
                format='dd/MM/yyyy'
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    placeholder: 'Chọn ngày xuất kho',
                    sx: sharedFormStyles.datePickerArrowAlign,
                  },
                }}
              />
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Kho xuất' labelWidth={100}>
              <TextField
                select
                size='small'
                value={formData.store_id}
                onChange={(event) => onChangeForm({ store_id: event.target.value })}
                disabled={readOnly}
                fullWidth
                sx={sharedFormStyles.inputArrowAlign}
              >
                {warehouseOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                ))}
              </TextField>
            </LabeledField>
            <LabeledField label='Trạng thái' labelWidth={100}>
              <Autocomplete
                disableClearable
                options={statusOptions}
                value={selectedStatus}
                onChange={(_event, newValue) => {
                  const nextStatus = newValue as { id: string; name: string } | null;
                  onChangeForm({ status: nextStatus?.id || '' });
                }}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                disabled={readOnly}
                renderOption={(props, option) => (
                  <li {...props} key={option.id} style={{ padding: 0, margin: 0 }}>
                    <Typography sx={{ color: option.color || 'inherit', paddingLeft: '24px', py: 0.5 }}>
                      {option.name}
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder='Chọn trạng thái'
                    size='small'
                    variant='standard'
                    InputProps={{
                      ...params.InputProps,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: selectedStatus?.color || 'inherit',
                        fontWeight: selectedStatus?.color ? 500 : 'inherit',
                      },
                    }}
                  />
                )}
                noOptionsText='Không tìm thấy trạng thái'
                fullWidth
              />
            </LabeledField>
            <LabeledField label='Nhân viên XK' labelWidth={100}>
              <AddButtonAutocomplete
                options={employeeOptions}
                value={employeeOptions.find((o) => o.id === formData.issued_by_id) || (formData.issued_by_id ? { id: formData.issued_by_id, name: formData.issued_by_name || '' } : null)}
                onChange={(_e, newVal) => {
                  const v = newVal as RequestMaterialEmployeeOption | null;
                  onChangeForm({ issued_by_id: v?.id || '', issued_by_name: v?.name || '' });
                }}
                placeholder='Chọn hoặc tìm nhân viên'
                noOptionsText='Không tìm thấy nhân viên'
                getOptionLabel={(opt: any) => formatEmployeeName(opt?.name)}
                renderOption={(props, option) => (
                  <Box component='li' {...props}>
                    <UserOptionDisplay user={option} />
                  </Box>
                )}
                disabled={readOnly}
                fullWidth
              />
            </LabeledField>
            <LabeledField label='Nhân viên nhận' labelWidth={100}>
              <AddButtonAutocomplete
                options={employeeOptions}
                value={employeeOptions.find((o) => o.id === formData.received_by_id) || (formData.received_by_id ? { id: formData.received_by_id, name: formData.received_by_name || '' } : null)}
                onChange={(_e, newVal) => {
                  const v = newVal as RequestMaterialEmployeeOption | null;
                  onChangeForm({ received_by_id: v?.id || '', received_by_name: v?.name || '' });
                }}
                placeholder='Chọn hoặc tìm nhân viên'
                noOptionsText='Không tìm thấy nhân viên'
                getOptionLabel={(opt: any) => formatEmployeeName(opt?.name)}
                renderOption={(props, option) => (
                  <Box component='li' {...props}>
                    <UserOptionDisplay user={option} />
                  </Box>
                )}
                disabled={readOnly}
                fullWidth
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ width: '100%' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Danh sách vật tư xuất</Typography>
        <IssueLinesTable
          lines={formData.lines || []}
          itemOptions={itemOptions}
          uomOptionsByLineId={uomOptionsByLineId}
          itemLoading={itemLoading}
          stockByLineId={stockByLineId}
          stockLoadingByLineId={stockLoadingByLineId}
          stockErrorByLineId={stockErrorByLineId}
          companyId={formData.company_id}
          onChangeLine={onChangeLine}
          onAddLine={onAddLine}
          onRemoveLine={onRemoveLine}
          onCreateItem={onCreateItem}
          canCreateItem={canCreateItem}
          searchItems={searchItems}
          searchLoading={searchLoading}
          disabled={readOnly}
        />
      </Box>

      <Box sx={{ mt: 2, width: '100%' }}>
        <LabeledField label='Ghi chú' alignItems='start' labelWidth={80}>
        <TextareaAutosize
          minRows={4}
          value={formData.note}
          onChange={(event) => onChangeForm({ note: event.target.value })}
          disabled={readOnly}
          placeholder='Nhập ghi chú cho phiếu xuất...'
          style={sharedFormStyles.additionalTextarea(canEdit)}
        />
        </LabeledField>
      </Box>

      <Box sx={{ mt: 2, width: '100%' }}>
        <LabeledField label='File đính kèm' labelWidth={100}>
          <FileAttachmentField
            documents={uploadedDocuments}
            selectedFiles={selectedFiles}
            disabled={readOnly || saving}
            loading={loadingDocuments}
            fileInputKey={`issue-file-input-${issueId || 'new'}`}
            onFileSelect={handleFileSelect}
            onRemoveFile={(index) => { setPendingDeleteType('file'); setPendingDeleteIndex(index); }}
            onRemoveDocument={(documentId) => { setPendingDeleteType('document'); setPendingDeleteDocId(documentId); }}
            onPreview={(documentId) => { void handleDocumentDownload(documentId, true); }}
            onDownload={(documentId) => { void handleDocumentDownload(documentId); }}
          />
        </LabeledField>
      </Box>

      <DeleteConfirmationModal
        open={pendingDeleteType !== null}
        onClose={() => { setPendingDeleteType(null); setPendingDeleteIndex(null); setPendingDeleteDocId(null); }}
        onConfirm={() => { void confirmDeleteAttachment(); }}
        title='Xóa file đính kèm'
        message='Bạn có chắc chắn muốn xóa file này?'
        confirmText='Xóa'
        cancelText='Hủy'
      />
    </AppForm>

    {printLoading && (
      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={32} />
          <Typography variant='body2' color='text.secondary'>Đang tải mẫu in...</Typography>
        </Box>
      </Box>
    )}

    <Box sx={{ position: 'fixed', left: '-9999px', top: 0 }}>
      <div ref={printRef}>
        <OrderPrintTemplateRuntime
          key={printKey}
          printType={printType}
          onReady={handlePrintReady}
          orderData={buildPrintData()}
        />
      </div>
    </Box>
    </>
  );
}
